/**
 * Inbox — DESIGN.md §7.5 (R28).
 *
 * "In-app notification list (unread bold), grouped by card, mark-read /
 * mark-all-read, click-through to the card. Mirrors whatever email would tell
 * you, so email can stay on digest without fear of missing things."
 */
import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Inbox as InboxIcon } from 'lucide-react';
import type { Notification } from '@slate/shared';
import { Page } from '@/components/page';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingRegion } from '@/components/ui/spinner';
import { ActivityLine } from '@/components/cards/activity-line';
import { useMarkRead, useNotifications } from '@/features/activity/api';
import { useCurrentUser } from '@/lib/auth';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/inbox')({
  component: InboxPage,
});

function InboxPage(): JSX.Element {
  const me = useCurrentUser();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const query = useNotifications(unreadOnly);
  const markRead = useMarkRead();

  const notifications = query.data?.pages.flatMap((page) => page.items) ?? [];
  const unread = notifications.filter((item) => !item.readAt).length;

  /** §7.5 — "grouped by card". Cardless events keep their own bucket. */
  const groups = useMemo(() => groupByCard(notifications), [notifications]);

  const markGroupRead = (group: Group): void => {
    const ids = group.items.filter((item) => !item.readAt).map((item) => item.id);
    if (ids.length > 0) markRead.mutate({ ids });
  };

  return (
    <Page
      title="Inbox"
      description="Everything you would have been emailed about, in one place."
      actions={
        <>
          <Button variant="ghost" size="sm" onClick={() => setUnreadOnly((value) => !value)}>
            {unreadOnly ? 'Show all' : 'Unread only'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={unread === 0 || markRead.isPending}
            onClick={() => markRead.mutate({ all: true })}
          >
            Mark all read
          </Button>
        </>
      }
    >
      {query.isLoading ? (
        <LoadingRegion label="Loading your inbox" />
      ) : groups.length === 0 ? (
        <EmptyState
          icon={<InboxIcon className="size-8" />}
          title={unreadOnly ? 'Nothing unread' : 'Your inbox is empty'}
          description="You are notified about cards you collaborate on — cards you created, were assigned, were mentioned on, or commented on. Nothing here means nothing has changed."
        />
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <section key={group.key} className="rounded-lg border">
              <header className="flex items-center justify-between gap-2 border-b px-3 py-2">
                {group.task ? (
                  <Link
                    to="/card/$number"
                    params={{ number: String(group.task.number) }}
                    // #52 — following a card's notifications reads them. The
                    // card would mark them read on arrival anyway, but doing it
                    // here means the badge has already dropped by the time the
                    // card paints, rather than a beat afterwards.
                    onClick={() => markGroupRead(group)}
                    className="min-w-0 truncate text-sm font-medium hover:underline"
                  >
                    <span className="text-muted-foreground font-mono text-xs">
                      #{group.task.number}
                    </span>{' '}
                    {group.task.title}
                  </Link>
                ) : (
                  <span className="text-sm font-medium">Other activity</span>
                )}
                {group.items.some((item) => !item.readAt) ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => markGroupRead(group)}
                  >
                    Mark read
                  </Button>
                ) : null}
              </header>
              <ul className="divide-y px-3">
                {group.items.map((item) => (
                  <li
                    key={item.id}
                    className={cn('flex items-start gap-2', !item.readAt && 'font-medium')}
                  >
                    {!item.readAt ? (
                      <span
                        className="bg-primary mt-4 size-1.5 shrink-0 rounded-full"
                        aria-label="Unread"
                      />
                    ) : (
                      <span className="mt-4 size-1.5 shrink-0" aria-hidden="true" />
                    )}
                    {/*
                      #52 — the line itself is the click target now.

                      "i can click and read the new information, it doesn't
                      update the inbox when i read this": the only link on a
                      group was its header, so a row was something to look at
                      rather than something to follow, and nothing about
                      looking at it was recorded. Reading one line marks that
                      one line read — not the whole card, which may be carrying
                      five other things you have not looked at.
                    */}
                    {item.task ? (
                      <Link
                        to="/card/$number"
                        params={{ number: String(item.task.number) }}
                        onClick={() => {
                          if (!item.readAt) markRead.mutate({ ids: [item.id] });
                        }}
                        className="hover:bg-accent/40 -mx-2 min-w-0 flex-1 rounded px-2"
                      >
                        <ActivityLine event={item.event} timezone={me.timezone} as="div" />
                      </Link>
                    ) : (
                      <div className="min-w-0 flex-1">
                        <ActivityLine event={item.event} timezone={me.timezone} as="div" />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}

          {query.hasNextPage ? (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                onClick={() => void query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Page>
  );
}

interface Group {
  key: string;
  task: Notification['task'];
  items: Notification[];
}

function groupByCard(notifications: Notification[]): Group[] {
  const groups = new Map<string, Group>();
  for (const notification of notifications) {
    const key = notification.task?.id ?? 'other';
    const existing = groups.get(key);
    if (existing) {
      existing.items.push(notification);
      continue;
    }
    groups.set(key, { key, task: notification.task, items: [notification] });
  }
  return [...groups.values()];
}
